import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import { Button, Checkbox, IconButton, List, ListItem, ListItemText, Stack, TextField, Tooltip } from "@mui/material";
import { GRID_CHECKBOX_SELECTION_FIELD, DataGrid as MuiDataGrid } from "@mui/x-data-grid";
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { filterFieldOf, matchesFilters, toFilterParams } from "./filterFields";
import { NoRecords } from "./NoRecords";
/**
 * Format a grid state into API query params: raw filter values become
 * `toFilterParams` output, the sort model becomes `sort`/`dir`, and the
 * pagination model becomes `skip`/`take`. The single place filter formatting
 * happens, so callers just feed the result to their data hook / export URL.
 */ export const buildGridQuery = (filterDefs, { filters, pagination, sort })=>({
        ...toFilterParams(filterDefs, filters),
        dir: sort[0]?.sort ?? 'desc',
        skip: pagination.page * pagination.pageSize,
        sort: sort[0]?.field,
        take: pagination.pageSize
    });
const CHECKBOX_COLUMN_WIDTH = 50;
const DEFAULT_COLUMN_WIDTH = 100;
/** the grid draws column separators at z-index 30, so a pinned header has to
    clear that to stop a scrolled-under separator painting over it. Both values
    match the ones the Pro grid uses for its own pinned sections. */ const PINNED_CELL_Z = 30;
const PINNED_HEADER_Z = 40;
/**
 * Offsets are derived from the declared column width. A flex column has no
 * fixed width to read, so only the edge-most pin on each side (offset 0) is
 * exact for those. Pinning more than one column per edge wants an explicit
 * `width` on every pinned column.
 */ const widthOf = (columns, field)=>{
    if (field === GRID_CHECKBOX_SELECTION_FIELD) {
        return CHECKBOX_COLUMN_WIDTH;
    }
    const column = columns.find((col)=>col.field === field);
    return column?.width ?? column?.minWidth ?? DEFAULT_COLUMN_WIDTH;
};
/**
 * Reorder so pinned fields sit at the physical edges of the row, matching how
 * the Pro grid moves pinned columns out of the scrollable middle section.
 */ const orderPinnedColumns = (columns, pinned)=>{
    const left = pinned.left ?? [];
    const right = pinned.right ?? [];
    const byField = (field)=>columns.find((col)=>col.field === field);
    const pinnedFields = new Set([
        ...left,
        ...right
    ]);
    return [
        ...left.map(byField),
        ...columns.filter((col)=>!pinnedFields.has(col.field)),
        ...right.map(byField)
    ].filter((col)=>col !== undefined);
};
const isActionsColumn = (column)=>column?.field === 'actions' || column?.type === 'actions';
/** Column pinning is opt-in (it disables virtualization), with one exception:
 * a `checkboxSelection` grid always pins its selection column — a selection
 * column that scrolls out of view is never wanted. Six apps consume this
 * component; everything else must not change behaviour silently. */ const EMPTY_PINS = {};
const EMPTY_COLUMN_STATE = {
    pinned: {},
    visibility: {}
};
/** resize events stream while the separator is dragged; only the trailing edge is worth persisting */ const WIDTH_COMMIT_DEBOUNCE_MS = 300;
const columnStateKey = (gridId)=>`ijoin-dataGrid-columns:${gridId}`;
const readColumnState = (key)=>{
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch  {
        /** corrupted or blocked storage degrades to the column defs' defaults */ return null;
    }
};
/** removed or renamed fields must not linger: keep only what the current column defs declare */ const pruneColumnState = (state, fields)=>({
        ...state.order && {
            order: state.order.filter((field)=>fields.has(field))
        },
        ...state.widths && {
            widths: Object.fromEntries(Object.entries(state.widths).filter(([field])=>fields.has(field)))
        },
        pinned: {
            left: (state.pinned.left ?? []).filter((field)=>fields.has(field)),
            right: (state.pinned.right ?? []).filter((field)=>fields.has(field))
        },
        visibility: Object.fromEntries(Object.entries(state.visibility).filter(([field])=>fields.has(field)))
    });
/** stored order first (pruned fields drop out), then any fields the order has never seen, in def order */ const orderedFields = (columns, order)=>{
    const defFields = columns.map((column)=>column.field);
    if (!order) {
        return defFields;
    }
    const known = order.filter((field)=>defFields.includes(field));
    return [
        ...known,
        ...defFields.filter((field)=>!known.includes(field))
    ];
};
const ColumnSettingsContext = /*#__PURE__*/ createContext(null);
const pinEdgeOf = (pinned, field)=>pinned.left?.includes(field) ? 'left' : pinned.right?.includes(field) ? 'right' : null;
const nextPinEdge = (edge)=>edge === null ? 'left' : edge === 'left' ? 'right' : null;
const PinStateIcon = (props)=>props.edge ? /*#__PURE__*/ _jsx(PushPinIcon, {
        sx: {
            transform: props.edge === 'left' ? 'rotate(90deg)' : 'rotate(-90deg)'
        }
    }) : /*#__PURE__*/ _jsx(PushPinOutlinedIcon, {});
/** replaces the grid's columns management panel: search, show/hide all, per-row visibility + pin cycling, drag-to-reorder (synced with header drags), and a reset: the AG Grid tool-panel model */ const ColumnsManagementPanel = ()=>{
    const settings = useContext(ColumnSettingsContext);
    const [dragField, setDragField] = useState(null);
    const [pressing, setPressing] = useState(false);
    const [previewOrder, setPreviewOrder] = useState(null);
    const [search, setSearch] = useState('');
    const rowRefs = useRef(new Map());
    const prevTops = useRef(new Map());
    /* FLIP: reordered DOM nodes repaint in place, so animate each moved row from its previous position to its new one */ useLayoutEffect(()=>{
        const tops = new Map();
        rowRefs.current.forEach((el, field)=>{
            const { top } = el.getBoundingClientRect();
            const prev = prevTops.current.get(field);
            if (prev !== undefined && Math.abs(prev - top) > 1) {
                el.animate([
                    {
                        transform: `translateY(${prev - top}px)`
                    },
                    {
                        transform: 'translateY(0)'
                    }
                ], {
                    duration: 180,
                    easing: 'ease'
                });
            }
            tops.set(field, top);
        });
        prevTops.current = tops;
    });
    if (!settings) {
        return null;
    }
    const byField = new Map(settings.columns.map((column)=>[
            column.field,
            column
        ]));
    const labelOf = (field)=>byField.get(field)?.headerName || field;
    const baseFields = orderedFields(settings.columns, settings.state.order).filter((field)=>field !== GRID_CHECKBOX_SELECTION_FIELD);
    /** while a drag is live the list renders the preview, so rows visibly resequence under the cursor */ const fields = previewOrder ?? baseFields;
    const visibleFields = search ? fields.filter((field)=>labelOf(field).toLowerCase().includes(search.toLowerCase())) : fields;
    const isShown = (field)=>settings.state.visibility[field] !== false;
    const allShown = fields.every(isShown);
    const noneShown = fields.every((field)=>!isShown(field));
    const onToggleAll = ()=>settings.commit({
            ...settings.state,
            visibility: Object.fromEntries(fields.map((field)=>[
                    field,
                    !allShown
                ]))
        });
    const onDragEnterRow = (target)=>{
        if (!dragField || dragField === target) {
            return;
        }
        const next = fields.filter((field)=>field !== dragField);
        next.splice(next.indexOf(target), 0, dragField);
        setPreviewOrder(next);
    };
    const onDragFinish = (commitPreview)=>{
        if (commitPreview && dragField && previewOrder) {
            settings.commit({
                ...settings.state,
                order: previewOrder
            });
        }
        setDragField(null);
        setPressing(false);
        setPreviewOrder(null);
    };
    /** while the mouse is held (pressing or mid-drag) every row tooltip is forced shut */ const tipProps = pressing || dragField ? {
        open: false
    } : {};
    return /*#__PURE__*/ _jsxs(Stack, {
        spacing: 1,
        sx: {
            p: 1,
            width: 320
        },
        children: [
            /*#__PURE__*/ _jsx(TextField, {
                "data-testid": "searchColumns",
                onChange: (event)=>setSearch(event.target.value),
                placeholder: "Search columns",
                size: "small",
                value: search
            }),
            /*#__PURE__*/ _jsxs(Stack, {
                alignItems: "center",
                direction: "row",
                sx: {
                    gap: 1,
                    px: 2
                },
                children: [
                    /*#__PURE__*/ _jsx(Tooltip, {
                        title: allShown ? 'Hide all columns' : 'Show all columns',
                        children: /*#__PURE__*/ _jsx(Checkbox, {
                            checked: allShown,
                            "data-testid": "toggleAllColumns",
                            edge: "start",
                            indeterminate: !allShown && !noneShown,
                            onChange: onToggleAll
                        })
                    }),
                    /*#__PURE__*/ _jsx(ListItemText, {
                        children: "Show/hide all"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(List, {
                "data-testid": "columnsManagement",
                dense: true,
                onMouseDownCapture: ()=>setPressing(true),
                onMouseUpCapture: ()=>setPressing(false),
                sx: {
                    maxHeight: '50vh',
                    overflowY: 'auto',
                    p: 0
                },
                children: visibleFields.map((field)=>{
                    const visible = isShown(field);
                    const edge = pinEdgeOf(settings.state.pinned, field);
                    return /*#__PURE__*/ _jsxs(ListItem, {
                        draggable: !search,
                        onDragEnd: ()=>onDragFinish(false),
                        onDragEnter: ()=>onDragEnterRow(field),
                        onDragOver: (event)=>event.preventDefault(),
                        onDragStart: ()=>setDragField(field),
                        onDrop: ()=>onDragFinish(true),
                        ref: (el)=>{
                            if (el) {
                                rowRefs.current.set(field, el);
                            } else {
                                rowRefs.current.delete(field);
                            }
                        },
                        secondaryAction: /*#__PURE__*/ _jsx(Tooltip, {
                            ...tipProps,
                            title: edge === null ? 'Pin to left' : edge === 'left' ? 'Pin to right' : 'Unpin',
                            children: /*#__PURE__*/ _jsx(IconButton, {
                                color: edge ? 'primary' : 'default',
                                "data-testid": `pinColumn-${field}`,
                                edge: "end",
                                onClick: ()=>settings.setPin(field, nextPinEdge(edge)),
                                children: /*#__PURE__*/ _jsx(PinStateIcon, {
                                    edge: edge
                                })
                            })
                        }),
                        sx: {
                            '& .IjoinDragHandle': {
                                opacity: 0,
                                transition: 'opacity 120ms'
                            },
                            '&:hover .IjoinDragHandle': {
                                opacity: search ? 0 : 1
                            },
                            cursor: search ? 'default' : 'grab',
                            gap: 1,
                            opacity: dragField === field ? 0.4 : 1,
                            py: 0
                        },
                        children: [
                            /*#__PURE__*/ _jsx(Tooltip, {
                                ...tipProps,
                                title: "Drag to reorder",
                                children: /*#__PURE__*/ _jsx(DragIndicatorIcon, {
                                    className: "IjoinDragHandle",
                                    color: "disabled"
                                })
                            }),
                            /*#__PURE__*/ _jsx(Tooltip, {
                                ...tipProps,
                                title: visible ? 'Hide column' : 'Show column',
                                children: /*#__PURE__*/ _jsx(Checkbox, {
                                    checked: visible,
                                    "data-testid": `toggleColumn-${field}`,
                                    edge: "start",
                                    onChange: ()=>settings.commit({
                                            ...settings.state,
                                            visibility: {
                                                ...settings.state.visibility,
                                                [field]: !visible
                                            }
                                        })
                                })
                            }),
                            /*#__PURE__*/ _jsx(ListItemText, {
                                children: labelOf(field)
                            })
                        ]
                    }, field);
                })
            }),
            /*#__PURE__*/ _jsx(Button, {
                "data-testid": "resetColumns",
                fullWidth: true,
                onClick: settings.reset,
                variant: "outlined",
                children: "Reset columns"
            })
        ]
    });
};
/**
 * Convenience pins for a caller that wants the common layout: leading column
 * left, trailing actions column right. Callers opt in explicitly by spreading
 * this into `pinnedColumns`; it is never applied automatically.
 */ export const leadAndActionsPins = (columns)=>{
    const first = columns[0];
    const last = columns[columns.length - 1];
    const pinsRight = !!last && isActionsColumn(last) && last !== first;
    return {
        left: first && !isActionsColumn(first) ? [
            first.field
        ] : [],
        right: pinsRight ? [
            last.field
        ] : []
    };
};
const applyPinRule = (rules, params)=>{
    const { edge, field, isBoundary, offset } = params;
    const cellSelector = `.MuiDataGrid-cell[data-field="${field}"]`;
    const header = `& .MuiDataGrid-columnHeader[data-field="${field}"]`;
    /* the separator between the pinned and scrollable sections, matching the
       Pro grid's `border` variant. A solid line is uniform down its whole
       length, so unlike a blurred shadow it can sit on the cells themselves
       without scalloping at each row border. */ const separator = isBoundary ? {
        [edge === 'left' ? 'borderRight' : 'borderLeft']: '1px solid var(--DataGrid-rowBorderColor)'
    } : {};
    rules[`& ${cellSelector}`] = {
        backgroundColor: 'background.paper',
        [edge]: offset,
        position: 'sticky',
        zIndex: PINNED_CELL_Z,
        ...separator
    };
    /* let the pinned cell adopt whatever background the row is showing, so
       hover and selection stay visually continuous across the pin boundary.
       The row is a descendant of the grid root, so `&` has to lead the
       selector or the generated rule can never match. */ rules[`&& .MuiDataGrid-row:hover ${cellSelector}`] = {
        backgroundColor: 'inherit'
    };
    rules[`&& .MuiDataGrid-row.Mui-selected ${cellSelector}`] = {
        backgroundColor: 'inherit'
    };
    rules[header] = {
        background: 'var(--DataGrid-t-header-background-base)',
        [edge]: offset,
        position: 'sticky',
        zIndex: PINNED_HEADER_Z,
        ...separator
    };
};
const pinnedColumnSx = (columns, pinned)=>{
    const left = pinned.left ?? [];
    const right = pinned.right ?? [];
    const rules = {};
    left.forEach((field, index)=>applyPinRule(rules, {
            edge: 'left',
            field,
            isBoundary: index === left.length - 1,
            offset: left.slice(0, index).reduce((sum, prior)=>sum + widthOf(columns, prior), 0)
        }));
    right.forEach((field, index)=>applyPinRule(rules, {
            edge: 'right',
            field,
            isBoundary: index === 0,
            offset: right.slice(index + 1).reduce((sum, later)=>sum + widthOf(columns, later), 0)
        }));
    return rules;
};
const emptyFilterValues = (filterDefs)=>Object.fromEntries(filterDefs.flatMap((def)=>'startName' in def ? [
            [
                def.startName,
                ''
            ],
            [
                def.endName,
                ''
            ]
        ] : [
            [
                def.name,
                def.type === 'multiSelect' ? [] : ''
            ]
        ]));
export const DataGrid = ({ filterDefs, initialColumnVisibility, initialFilters, initialSort, onClear, onDownload, onSearch, pageSize, pinnedColumns, showColumnMenu, sx, ...props })=>{
    const gridId = props.slotProps?.root?.['data-testid'] ?? 'grid';
    const storageKey = columnStateKey(gridId);
    const fieldSignature = props.columns.map((column)=>column.field).join('|');
    const [columnState, setColumnState] = useState(()=>{
        if (!showColumnMenu) {
            return EMPTY_COLUMN_STATE;
        }
        const stored = readColumnState(storageKey);
        return pruneColumnState(stored ?? {
            pinned: pinnedColumns ?? EMPTY_PINS,
            visibility: initialColumnVisibility ?? {}
        }, new Set(fieldSignature.split('|')));
    });
    /* prune persisted entries when the column defs change (fields removed or renamed) */ useEffect(()=>{
        if (!showColumnMenu) {
            return;
        }
        const stored = readColumnState(storageKey);
        if (!stored) {
            return;
        }
        const pruned = pruneColumnState(stored, new Set(fieldSignature.split('|')));
        if (JSON.stringify(pruned) !== JSON.stringify(stored)) {
            localStorage.setItem(storageKey, JSON.stringify(pruned));
            setColumnState(pruned);
        }
    }, [
        fieldSignature,
        showColumnMenu,
        storageKey
    ]);
    const columnStateRef = useRef(columnState);
    useEffect(()=>{
        columnStateRef.current = columnState;
    });
    const pendingWidthsRef = useRef({});
    const widthCommitRef = useRef(null);
    useEffect(()=>()=>{
            if (widthCommitRef.current) {
                clearTimeout(widthCommitRef.current);
            }
        }, []);
    const commitColumnState = (next)=>{
        setColumnState(next);
        try {
            localStorage.setItem(storageKey, JSON.stringify(next));
        } catch  {
        /** private-mode or quota failures keep the in-session state working */ }
    };
    const containerRef = useRef(null);
    const dragFieldRef = useRef(null);
    const commitOrderRef = useRef(()=>undefined);
    useEffect(()=>{
        commitOrderRef.current = (source, target)=>{
            const next = orderedFields(props.columns, columnState.order).filter((field)=>field !== source);
            next.splice(next.indexOf(target), 0, source);
            commitColumnState({
                ...columnState,
                order: next
            });
        };
    });
    /* the community grid force-disables its Pro column reorder, so emulate it: native HTML5 drag on the rendered header cells, committed into the stored order */ useEffect(()=>{
        const container = containerRef.current;
        if (!showColumnMenu || !container) {
            return;
        }
        const headerOf = (target)=>target?.closest?.('.MuiDataGrid-columnHeader[data-field]');
        const onDragStart = (event)=>{
            const header = headerOf(event.target);
            if (!header) {
                return;
            }
            dragFieldRef.current = header.dataset.field ?? null;
            event.dataTransfer?.setData('text/plain', dragFieldRef.current ?? '');
        };
        const onDragOver = (event)=>{
            if (dragFieldRef.current && headerOf(event.target)) {
                event.preventDefault();
            }
        };
        const onDrop = (event)=>{
            const target = headerOf(event.target)?.dataset.field;
            const source = dragFieldRef.current;
            dragFieldRef.current = null;
            if (!source || !target || source === target) {
                return;
            }
            event.preventDefault();
            commitOrderRef.current(source, target);
        };
        /* virtualization mounts headers lazily, so keep newly rendered cells draggable */ const markDraggable = ()=>container.querySelectorAll('.MuiDataGrid-columnHeader[data-field]').forEach((el)=>{
                el.draggable = true;
            });
        markDraggable();
        const observer = new MutationObserver(markDraggable);
        observer.observe(container, {
            childList: true,
            subtree: true
        });
        container.addEventListener('dragstart', onDragStart);
        container.addEventListener('dragover', onDragOver);
        container.addEventListener('drop', onDrop);
        return ()=>{
            observer.disconnect();
            container.removeEventListener('dragstart', onDragStart);
            container.removeEventListener('dragover', onDragOver);
            container.removeEventListener('drop', onDrop);
        };
    }, [
        showColumnMenu
    ]);
    const settingsValue = useMemo(()=>({
            columns: props.columns,
            commit: commitColumnState,
            reset: ()=>{
                try {
                    localStorage.removeItem(storageKey);
                } catch  {
                /** blocked storage still resets the in-session state */ }
                setColumnState({
                    pinned: pinnedColumns ?? EMPTY_PINS,
                    visibility: initialColumnVisibility ?? {}
                });
            },
            setPin: (field, edge)=>{
                const left = (columnState.pinned.left ?? []).filter((f)=>f !== field);
                const right = (columnState.pinned.right ?? []).filter((f)=>f !== field);
                if (edge === 'left') {
                    left.push(field);
                }
                if (edge === 'right') {
                    right.push(field);
                }
                commitColumnState({
                    ...columnState,
                    pinned: {
                        left,
                        right
                    }
                });
            },
            state: columnState
        }), // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        columnState,
        initialColumnVisibility,
        pinnedColumns,
        props.columns,
        storageKey
    ]);
    const resolvedPins = useMemo(()=>{
        const stored = showColumnMenu ? columnState.pinned : pinnedColumns ?? EMPTY_PINS;
        /** hidden columns must leave the pin math entirely, or later pins keep stale sticky offsets */ const pins = showColumnMenu ? {
            left: (stored.left ?? []).filter((field)=>columnState.visibility[field] !== false),
            right: (stored.right ?? []).filter((field)=>columnState.visibility[field] !== false)
        } : stored;
        const pinsCheckbox = props.checkboxSelection && pins.left?.[0] !== GRID_CHECKBOX_SELECTION_FIELD;
        return pinsCheckbox ? {
            ...pins,
            left: [
                GRID_CHECKBOX_SELECTION_FIELD,
                ...pins.left ?? []
            ]
        } : pins;
    }, [
        columnState.pinned,
        columnState.visibility,
        pinnedColumns,
        props.checkboxSelection,
        showColumnMenu
    ]);
    const hasPinned = !!(resolvedPins.left?.length || resolvedPins.right?.length);
    /* stable identities: a fresh columns array every render makes the grid tear
     down and rebuild its column state, which leaves the render context stale */ const orderedColumns = useMemo(()=>{
        /** stored widths override the defs (and displace flex, which would fight a fixed width) */ const sized = showColumnMenu ? props.columns.map((column)=>{
            const width = columnState.widths?.[column.field];
            return width ? {
                ...column,
                flex: undefined,
                width
            } : column;
        }) : props.columns;
        const byField = new Map(sized.map((column)=>[
                column.field,
                column
            ]));
        const base = showColumnMenu ? orderedFields(sized, columnState.order).map((field)=>byField.get(field)) : sized;
        return hasPinned ? orderPinnedColumns(base, resolvedPins) : base;
    }, [
        columnState.order,
        columnState.widths,
        hasPinned,
        props.columns,
        resolvedPins,
        showColumnMenu
    ]);
    /** filters mirror the columns: a hidden column hides its filter, and matched filters re-sort to the visual column order; defs without a matching column keep their slots */ const arrangedFilterDefs = useMemo(()=>{
        if (!showColumnMenu || !filterDefs) {
            return filterDefs;
        }
        const orderIndex = new Map(orderedColumns.map((column, index)=>[
                column.field,
                index
            ]));
        const visibleDefs = filterDefs.filter((def)=>{
            const field = filterFieldOf(def);
            return !orderIndex.has(field) || columnState.visibility[field] !== false;
        });
        const matched = visibleDefs.filter((def)=>orderIndex.has(filterFieldOf(def))).sort((a, b)=>(orderIndex.get(filterFieldOf(a)) ?? 0) - (orderIndex.get(filterFieldOf(b)) ?? 0));
        let matchIndex = 0;
        return visibleDefs.map((def)=>orderIndex.has(filterFieldOf(def)) ? matched[matchIndex++] : def);
    }, [
        columnState.visibility,
        filterDefs,
        orderedColumns,
        showColumnMenu
    ]);
    const [gridState, setGridState] = useState({
        filters: {
            ...emptyFilterValues(filterDefs ?? []),
            ...initialFilters
        },
        pagination: {
            page: 0,
            pageSize: pageSize ?? 50
        },
        sort: initialSort ?? []
    });
    const managed = !!onSearch;
    /* filterDefs without onSearch: the toolbar still drives the filter state, and the rows are filtered here */ const isClientFiltered = !!filterDefs && !managed;
    const resetPage = (next)=>({
            ...next,
            pagination: {
                ...next.pagination,
                page: 0
            }
        });
    /* commit a new grid state and emit the formatted query to the given callback,
     so callers receive already-built params (never the raw grid state) */ const commit = (next, notify)=>{
        setGridState(next);
        notify?.(buildGridQuery(arrangedFilterDefs ?? [], next));
    };
    useEffect(()=>{
        onSearch?.(buildGridQuery(arrangedFilterDefs ?? [], gridState));
    // fetch the initial page once on mount; later changes emit via handlers
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const stateProps = managed ? {
        onPaginationModelChange: (pagination)=>commit({
                ...gridState,
                pagination
            }, onSearch),
        onSortModelChange: (sort)=>commit({
                ...gridState,
                sort
            }, onSearch),
        paginationModel: gridState.pagination,
        sortModel: gridState.sort
    } : {};
    const managedToolbarProps = managed || isClientFiltered ? {
        filterDefs: arrangedFilterDefs,
        filterValues: gridState.filters,
        onApplyFilters: (values)=>commit(resetPage({
                ...gridState,
                filters: values
            }), onSearch),
        onClearAll: ()=>commit(resetPage({
                ...gridState,
                filters: emptyFilterValues(filterDefs ?? [])
            }), managed ? onClear : undefined),
        onDownload: onDownload ? ()=>onDownload(buildGridQuery(arrangedFilterDefs ?? [], gridState)) : undefined,
        onFilterChange: (patch)=>commit(resetPage({
                ...gridState,
                filters: {
                    ...gridState.filters,
                    ...patch
                }
            }), onSearch)
    } : {};
    const toolbarSlotProps = {
        chipDeleteIcon: /*#__PURE__*/ _jsx(CloseOutlinedIcon, {}),
        showColumnsButton: showColumnMenu,
        ...managedToolbarProps,
        ...props.slotProps?.toolbar
    };
    /* The checkbox selection column is always the leftmost pin, and it pins by
     default: a selection column that scrolls out of view is never wanted.
     Folding it into the pin list here keeps the physical column order and the
     sticky offsets derived from the same list — ordering it separately is how
     the checkbox column ended up laid out as if the other pins didn't exist. */ const pinnedSx = useMemo(()=>hasPinned ? pinnedColumnSx(orderedColumns, resolvedPins) : undefined, [
        hasPinned,
        orderedColumns,
        resolvedPins
    ]);
    /* Column virtualization unmounts off-screen cells, which would destroy the
     pinned cell instead of pinning it. This has to be the declarative prop:
     the imperative `unstable_setColumnVirtualization` gets reset by the grid's
     own virtualization effect, which leaves trailing columns unrendered until
     a scroll forces a new render context. Note this also disables row
     virtualization, which only costs anything on a grid that opts out of the
     `autoHeight` default. */ const pinnedProps = hasPinned ? {
        columns: orderedColumns,
        disableVirtualization: props.disableVirtualization ?? true,
        sx: [
            pinnedSx,
            ...Array.isArray(sx) ? sx : [
                sx
            ]
        ]
    } : {
        ...showColumnMenu ? {
            columns: orderedColumns
        } : {},
        sx
    };
    const columnMenuProps = showColumnMenu ? {
        columnVisibilityModel: columnState.visibility,
        disableColumnMenu: true,
        onColumnVisibilityModelChange: (model)=>commitColumnState({
                ...columnState,
                visibility: model
            }),
        onColumnWidthChange: (params)=>{
            pendingWidthsRef.current = {
                ...pendingWidthsRef.current,
                [params.colDef.field]: params.width
            };
            if (widthCommitRef.current) {
                clearTimeout(widthCommitRef.current);
            }
            widthCommitRef.current = setTimeout(()=>{
                commitColumnState({
                    ...columnStateRef.current,
                    widths: {
                        ...columnStateRef.current.widths,
                        ...pendingWidthsRef.current
                    }
                });
                pendingWidthsRef.current = {};
            }, WIDTH_COMMIT_DEBOUNCE_MS);
        }
    } : {};
    return /*#__PURE__*/ _jsx(ColumnSettingsContext.Provider, {
        value: settingsValue,
        children: /*#__PURE__*/ _jsx("div", {
            ref: containerRef,
            style: {
                display: 'contents'
            },
            children: /*#__PURE__*/ _jsx(MuiDataGrid, {
                autoHeight: true,
                ...props,
                ...isClientFiltered && {
                    rows: props.rows?.filter((row)=>matchesFilters(arrangedFilterDefs ?? [], gridState.filters, row))
                },
                ...stateProps,
                ...columnMenuProps,
                slotProps: {
                    basePagination: {
                        material: {
                            showFirstButton: true,
                            showLastButton: true
                        }
                    },
                    ...props.slotProps,
                    loadingOverlay: {
                        noRowsVariant: 'skeleton',
                        variant: 'linear-progress',
                        ...props.slotProps?.loadingOverlay
                    },
                    toolbar: toolbarSlotProps
                },
                slots: {
                    noRowsOverlay: NoRecords,
                    ...showColumnMenu ? {
                        columnsManagement: ColumnsManagementPanel
                    } : {},
                    ...props.slots
                },
                ...pinnedProps
            })
        })
    });
};
DataGrid.displayName = 'DataGrid';

//# sourceMappingURL=DataGrid.js.map